import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2, AlertCircle, RefreshCw, Video } from 'lucide-react';

const JANUS_WS_URL = (window.location.protocol === 'https:' ? 'wss:' : 'ws:') + '//' + window.location.host + '/ws/janus';

const JanusPlayer = ({ 
  streamId, 
  cameraName = 'Camera',
  autoPlay = true,
  muted = true,
  onError,
  onPlaying,
  className = ''
}) => {
  const videoRef = useRef(null);
  const pcRef = useRef(null);
  const wsRef = useRef(null);
  const sessionIdRef = useRef(null);
  const handleIdRef = useRef(null);
  const keepAliveRef = useRef(null);
  
  const [status, setStatus] = useState('idle'); // idle, connecting, playing, error
  const [errorMsg, setErrorMsg] = useState('');
  const transactionCounter = useRef(0);
  const mountedRef = useRef(true);

  const generateTransaction = () => `tx-${Date.now()}-${++transactionCounter.current}`;

  const cleanup = useCallback(() => {
    console.log(`[JanusPlayer] Cleanup for stream ${streamId}`);
    
    if (keepAliveRef.current) {
      clearInterval(keepAliveRef.current);
      keepAliveRef.current = null;
    }
    
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      // Try to cleanly destroy session
      if (sessionIdRef.current) {
        try {
          wsRef.current.send(JSON.stringify({
            janus: 'destroy',
            session_id: sessionIdRef.current,
            transaction: generateTransaction()
          }));
        } catch (e) {}
      }
      wsRef.current.close();
    }
    wsRef.current = null;
    sessionIdRef.current = null;
    handleIdRef.current = null;
  }, [streamId]);

  const sendMessage = useCallback((message, waitForEvent = false) => {
    return new Promise((resolve, reject) => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        reject(new Error('WebSocket not connected'));
        return;
      }
      const transaction = generateTransaction();
      const fullMessage = { ...message, transaction };
      const timeoutId = setTimeout(() => {
        wsRef.current?.removeEventListener('message', handleResponse);
        reject(new Error('Request timeout'));
      }, 15000);
      
      const handleResponse = (event) => {
        try {
          const response = JSON.parse(event.data);
          if (response.transaction === transaction) {
            if (response.janus === 'error') {
              clearTimeout(timeoutId);
              wsRef.current?.removeEventListener('message', handleResponse);
              reject(new Error(response.error?.reason || 'Janus error'));
              return;
            }
            
            // For 'message' requests, Janus sends 'ack' first, then 'event' with data
            if (waitForEvent) {
              if (response.janus === 'ack') {
                console.log('[JanusPlayer] Got ack, waiting for event...');
                return; // Keep waiting for event
              }
              if (response.janus === 'event' || response.jsep) {
                clearTimeout(timeoutId);
                wsRef.current?.removeEventListener('message', handleResponse);
                resolve(response);
                return;
              }
            } else {
              // For create/attach, resolve on first success
              clearTimeout(timeoutId);
              wsRef.current?.removeEventListener('message', handleResponse);
              resolve(response);
            }
          }
        } catch (e) {
          // Ignore parse errors for other messages
        }
      };
      wsRef.current.addEventListener('message', handleResponse);
      wsRef.current.send(JSON.stringify(fullMessage));
    });
  }, []);

  const startStreaming = useCallback(async () => {
    if (!mountedRef.current) return;
    
    try {
      setStatus('connecting');
      setErrorMsg('');
      cleanup();

      console.log(`[JanusPlayer] Connecting to stream ${streamId}...`);

      // Create WebSocket connection
      wsRef.current = new WebSocket(JANUS_WS_URL, 'janus-protocol');
      
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('WebSocket connection timeout')), 10000);
        wsRef.current.onopen = () => {
          clearTimeout(timeout);
          resolve();
        };
        wsRef.current.onerror = () => {
          clearTimeout(timeout);
          reject(new Error('WebSocket connection failed'));
        };
      });

      if (!mountedRef.current) return;
      console.log('[JanusPlayer] WebSocket connected');

      // Create session
      const sessionResponse = await sendMessage({ janus: 'create' });
      sessionIdRef.current = sessionResponse.data?.id;
      if (!sessionIdRef.current) throw new Error('Failed to create session');
      console.log(`[JanusPlayer] Session created: ${sessionIdRef.current}`);

      // Attach to streaming plugin
      const attachResponse = await sendMessage({
        janus: 'attach',
        session_id: sessionIdRef.current,
        plugin: 'janus.plugin.streaming'
      });
      handleIdRef.current = attachResponse.data?.id;
      if (!handleIdRef.current) throw new Error('Failed to attach to streaming plugin');
      console.log(`[JanusPlayer] Handle attached: ${handleIdRef.current}`);

      // Create peer connection with STUN
      // Fetch TURN credentials for better connectivity
      let iceServers = [{ urls: 'stun:stun.l.google.com:19302' }];
      try {
        const turnResponse = await fetch('/api/turn/credentials', { credentials: 'include' });
        if (turnResponse.ok) {
          const turnData = await turnResponse.json();
          if (turnData.iceServers) {
            iceServers = turnData.iceServers;
            console.log('[JanusPlayer] TURN credentials loaded');
          }
        }
      } catch (e) {
        console.warn('[JanusPlayer] TURN not available, using STUN only');
      }
      
      pcRef.current = new RTCPeerConnection({ iceServers });

      pcRef.current.ontrack = (event) => {
        console.log('[JanusPlayer] Got track:', event.track.kind);
        if (videoRef.current && event.streams[0]) {
          videoRef.current.srcObject = event.streams[0];
          if (mountedRef.current) {
            setStatus('playing');
            onPlaying?.();
          }
        }
      };

      pcRef.current.onicecandidate = (event) => {
        if (event.candidate && sessionIdRef.current && handleIdRef.current) {
          sendMessage({
            janus: 'trickle',
            session_id: sessionIdRef.current,
            handle_id: handleIdRef.current,
            candidate: event.candidate
          }).catch(err => console.warn('[JanusPlayer] Trickle error:', err));
        }
      };

      pcRef.current.oniceconnectionstatechange = () => {
        console.log('[JanusPlayer] ICE state:', pcRef.current?.iceConnectionState);
        if (pcRef.current?.iceConnectionState === 'failed') {
          if (mountedRef.current) {
            setStatus('error');
            setErrorMsg('Connection failed');
          }
        }
      };

      // Request to watch the stream
      console.log(`[JanusPlayer] Requesting stream ${streamId}...`);
      const watchResponse = await sendMessage({
        janus: 'message',
        session_id: sessionIdRef.current,
        handle_id: handleIdRef.current,
        body: { request: 'watch', id: streamId }
      }, true); // waitForEvent = true to get JSEP offer

      // Handle JSEP offer from Janus
      if (watchResponse.jsep) {
        console.log('[JanusPlayer] Got JSEP offer, creating answer...');
        await pcRef.current.setRemoteDescription(new RTCSessionDescription(watchResponse.jsep));
        const answer = await pcRef.current.createAnswer();
        await pcRef.current.setLocalDescription(answer);

        // Send answer back to Janus
        await sendMessage({
          janus: 'message',
          session_id: sessionIdRef.current,
          handle_id: handleIdRef.current,
          body: { request: 'start' },
          jsep: answer
        });
        console.log('[JanusPlayer] Answer sent, waiting for video...');
      } else if (watchResponse.plugindata?.data?.error) {
        throw new Error(watchResponse.plugindata.data.error);
      }

      // Set up message handler for events
      wsRef.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message.janus === 'hangup') {
            console.log('[JanusPlayer] Stream ended, reason:', message);
            if (mountedRef.current) {
              setStatus('error');
              setErrorMsg('Stream ended');
            }
          } else if (message.janus === 'slowlink') {
            console.warn('[JanusPlayer] Slow link detected');
          }
        } catch (e) {}
      };

      // Send keep-alive periodically
      keepAliveRef.current = setInterval(() => {
        if (sessionIdRef.current && wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({
            janus: 'keepalive',
            session_id: sessionIdRef.current,
            transaction: generateTransaction()
          }));
        }
      }, 25000);

    } catch (error) {
      console.error('[JanusPlayer] Error:', error);
      if (mountedRef.current) {
        setStatus('error');
        setErrorMsg(error.message);
        onError?.(error);
      }
    }
  }, [streamId, cleanup, sendMessage, onPlaying, onError]);

  useEffect(() => {
    mountedRef.current = true;
    
    if (autoPlay && streamId) {
      startStreaming();
    }
    
    return () => {
      mountedRef.current = false;
      cleanup();
    };
  }, [streamId, autoPlay, startStreaming, cleanup]);

  const handleRetry = () => {
    startStreaming();
  };

  return (
    <div className={`relative bg-black ${className}`} style={{ minHeight: '120px' }}>
      <video
        ref={videoRef}
        autoPlay
        muted={muted}
        playsInline
        className="w-full h-full object-contain"
      />
      
      {status === 'connecting' && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/70">
          <div className="flex flex-col items-center text-white">
            <Loader2 className="w-8 h-8 animate-spin mb-2" />
            <span className="text-sm">Connecting...</span>
          </div>
        </div>
      )}

      {status === 'idle' && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/70">
          <div className="flex flex-col items-center text-white">
            <Video className="w-8 h-8 mb-2 opacity-50" />
            <span className="text-sm opacity-50">Click to play</span>
          </div>
        </div>
      )}

      {status === 'error' && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80">
          <div className="flex flex-col items-center text-white text-center p-4">
            <AlertCircle className="w-8 h-8 text-red-400 mb-2" />
            <span className="text-sm mb-3">{errorMsg || 'Connection failed'}</span>
            <button
              onClick={handleRetry}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded text-sm"
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(JanusPlayer);
